import { useEffect, useRef } from 'react';
import { z } from 'zod/v3';
import { Label } from '@/components/shadcn/label.tsx';
import { RadioGroup, RadioGroupItem } from '@/components/shadcn/radio-group.tsx';
import { Textarea } from '@/components/shadcn/textarea.tsx';
import { govtIntakeFormApprovalRecommendations, govtIntakeFormRole } from '@/data/govtIntakeStaticData.ts';
import type { ValidationChange } from '@/modules/governmentIntake/base-entity.tsx';
import HeaderAndSub from '@/modules/governmentIntake/components/shared-component.tsx';
import { useIntakeForm } from '@/modules/governmentIntake/context/intake-form-context.tsx';
import { createFieldChange, useFormController } from '@/modules/governmentIntake/useFormController.ts';

const roleContextSchema = z
  .object({
    roleDescription: z.string().min(1, 'Role is required'),
    involvedInApprovalRecommendation: z.string(),
    fromSeinTerminalPerspective: z.string().min(1, 'This field is required'),
    successAfterTwelveMonth: z.string(),
  })
  .superRefine((data, ctx) => {
    if (data.roleDescription !== 'operator' && data.involvedInApprovalRecommendation.trim().length === 0) {
      ctx.addIssue({
        path: ['involvedInApprovalRecommendation'],
        code: 'custom',
        message: 'This field is required',
      });
    }
  });

type RoleAndContextData = z.infer<typeof roleContextSchema>;

export function SectionRoleAndContext({ onValidationChange }: ValidationChange) {
  const { data, updateRoleContext } = useIntakeForm();

  const onValidationChangeRef = useRef(onValidationChange);
  onValidationChangeRef.current = onValidationChange;

  const lastFormDataRef = useRef<string>('');

  const initialData = useRef<RoleAndContextData>({
    roleDescription: data.roleContext.roleDescription || '',
    involvedInApprovalRecommendation: data.roleContext.involvedInApprovalRecommendation || '',
    fromSeinTerminalPerspective: data.roleContext.fromSeinTerminalPerspective || '',
    successAfterTwelveMonth: data.roleContext.successAfterTwelveMonth || '',
  }).current;

  const form = useFormController<RoleAndContextData>({ schema: roleContextSchema, initialData });

  const onChange = createFieldChange<RoleAndContextData>(form);

  useEffect(() => {
    const serialized = JSON.stringify(form.data);
    if (lastFormDataRef.current === serialized) return;
    lastFormDataRef.current = serialized;
    updateRoleContext(form.data);
  }, [form.data, updateRoleContext]);

  useEffect(() => {
    onValidationChangeRef.current?.(form.isValid);
  }, [form.isValid]);

  const handleRoleDescriptionChange = (value: string) => {
    form.setField('roleDescription', value);
    if (value === 'operator') {
      form.setField('involvedInApprovalRecommendation', '');
    }
  };

  const handleApprovalRecommendationChange = (value: string) => {
    form.setField('involvedInApprovalRecommendation', value);
  };

  return (
    <div className='flex flex-col flex-1 justify-center'>
      <header className='pt-8'>
        <HeaderAndSub title='Role & Responsibilities' subTitle='Help us understand how work is executed in your department.' />
      </header>

      <section className='flex-1 overflow-y-auto pb-4'>
        <div className='space-y-6 pt-8'>
          <div>
            <Label className='text-sm font-semibold mb-3'>
              Which best describes your role in executing the work of your department? <span className='text-red-500'>*</span>
            </Label>
            <RadioGroup value={form.data.roleDescription} onValueChange={handleRoleDescriptionChange}>
              {govtIntakeFormRole.map((i) => (
                <div key={i.id} className='flex items-center gap-2'>
                  <RadioGroupItem value={i.id} id={`i-${i.id}`} />
                  <Label className='hover:cursor-pointer' htmlFor={`i-${i.id}`}>
                    {i.name}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {form.data.roleDescription !== 'operator' && (
            <div>
              <Label className='text-sm font-semibold mb-3'>Are you involved in approvals or recommendations related to this work?</Label>
              <RadioGroup value={form.data.involvedInApprovalRecommendation} onValueChange={handleApprovalRecommendationChange}>
                {govtIntakeFormApprovalRecommendations.map((i) => (
                  <div key={i.id} className='flex items-center gap-2'>
                    <RadioGroupItem value={i.id} id={`p-${i.id}`} />
                    <Label className='hover:cursor-pointer' htmlFor={`p-${i.id}`}>
                      {i.title}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
          )}

          <div>
            <Label className='text-sm font-semibold mb-1'>
              From your perspective, what problem should SEIN Terminal help solve for your department?{' '}
              <span className='text-red-500'>*</span>
            </Label>
            <Textarea
              value={form.data.fromSeinTerminalPerspective}
              onChange={onChange('fromSeinTerminalPerspective')}
              rows={4}
              placeholder='Describe the key problems or challanges...'
              className='w-full px-3 py-2 border-2 rounded-lg border-gray-300 resize-none'
            />
          </div>

          <div>
            <Label className='text-sm font-semibold mb-1'>
              What would success with SEIN Terminal look like for your department 12 months from now?
            </Label>
            <Textarea
              value={form.data.successAfterTwelveMonth}
              onChange={onChange('successAfterTwelveMonth')}
              rows={4}
              placeholder='Describe what success would look like...'
              className='w-full px-3 py-2 border-2 rounded-lg border-gray-300 resize-none'
            />
          </div>
        </div>
      </section>
    </div>
  );
}
